import { Box, CircularProgress, TextField } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import type { Option } from 'components/shared/DutyCodeSelector.types';
import { dutyCodeToRow } from '../HoursTable.utils';
import { useDutyLineOptions } from '../hooks/useDutyLineOptions';
import type { DutyLineOptionEditCellProps } from './DutyLineOptionEditCell.types';
import {
  StyledCustomPopperEditCell,
  StyledDutyLineAutocomplete,
  StyledShowMoreOption
} from './DutyLineOptionEditCell.styles';
import UncontrolledDutyLineList from './UncontrolledDutyLineList';
import UncontrolledList from './UncontrolledList';
import { cacheWaLabel, cacheDutyLabel } from './DutyLineOptionEditCell.cache';
import { useTranslation } from 'react-i18next';

export default function DutyLineOptionEditCell(
  props: DutyLineOptionEditCellProps
) {
  const { t } = useTranslation();
  const {
    id,
    field,
    value: currentValue,
    api,
    row,
    hasFocus,
    fetchKind,
    options: externalOptions,
    startDate,
    endDate,
    employeeId,
    organizationalUnitId
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    options,
    dutyCodes,
    showAll: _showAll,
    setShowAll,
    isFetching
  } = useDutyLineOptions({
    fetchKind,
    startDate,
    endDate,
    employeeId,
    date: row.dateKey,
    organizationalUnitId
  });

  // fall back to props.options for the “old” use-case
  const finalOptions = useMemo(
    () => (fetchKind ? options : externalOptions ?? []),
    [fetchKind, options, externalOptions]
  );

  const hasId = finalOptions.some((option) => 'id' in option);

  const current =
    finalOptions.find(
      (option) =>
        (hasId && option.id === currentValue) ||
        option.value === currentValue ||
        option.label === currentValue
    ) ?? null;

  const handleChange = async (
    event: React.SyntheticEvent,
    option: Option | null
  ) => {
    if (option?.value === '__SHOW_MORE__') return;
    const isValid = await api.setEditCellValue({
      id,
      field,
      value: option?.value ?? null
    });
    if (isValid) api.stopCellEditMode({ id, field });

    // special case: basic duty => update period / dutyTime / dutyType
    if (fetchKind === 'basicDuty') {
      const duty = dutyCodes.find((duty) => duty.dutyCodeId === option?.value);
      if (duty) {
        const { periods, dutyTime, dutyType } = dutyCodeToRow(duty);

        api.updateRows([{ id, periods, dutyTime, dutyType }]);

        api.setEditCellValue({
          id,
          field: 'periods',
          value: periods,
          debounceMs: 0
        });

        // make sure dutyType is saved immediately
        api.setEditCellValue({
          id,
          field: 'dutyType',
          value: dutyType,
          debounceMs: 0
        });
      }
    }
    event.stopPropagation();
  };

  // required to fix the focus of the input
  useEffect(() => {
    if (hasFocus) inputRef.current?.focus();
  }, [hasFocus]);

  useEffect(() => {
    if (fetchKind === 'workAgreement') {
      finalOptions.forEach((opt) => {
        if (typeof opt.value === 'number') {
          cacheWaLabel[opt.value] = opt.label;
        }
      });
    }

    if (fetchKind === 'basicDuty') {
      finalOptions.forEach((opt) => {
        if (typeof opt.value === 'number') {
          cacheDutyLabel[opt.value] = opt.label;
        }
      });
    }
  }, [finalOptions, fetchKind]);

  return (
    <StyledDutyLineAutocomplete
      options={finalOptions}
      value={current}
      onChange={handleChange}
      blurOnSelect
      autoHighlight={false}
      getOptionKey={(option) => String(option.key ?? crypto.randomUUID())}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) =>
        a.value !== '__SHOW_MORE__' &&
        b.value !== '__SHOW_MORE__' &&
        a.value === b.value
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        if (fetchKind === 'dutyHourTypes')
          return <UncontrolledList key={key} option={option} {...rest} />;

        return fetchKind === 'basicDuty' ? (
          option.value === '__SHOW_MORE__' ? (
            <StyledShowMoreOption
              key={key}
              {...rest}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowAll(true)}
            >
              {t('showMore')}
            </StyledShowMoreOption>
          ) : (
            <UncontrolledDutyLineList
              key={key}
              option={option}
              dutyCodes={dutyCodes}
              {...rest}
            />
          )
        ) : (
          <Box component="li" key={key} {...rest}>
            {option.label}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          inputRef={inputRef}
          slotProps={{
            ...params.InputProps,
            input: {
              ...params.InputProps,
              ref: params.InputProps.ref,
              startAdornment: (
                <>
                  {isFetching && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
      fullWidth
      slots={{
        popper: StyledCustomPopperEditCell
      }}
    />
  );
}
